import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Backpack, Bell, MessageCircle, Coins, Egg, Binoculars, ShoppingCart } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import InventoryBag from "@/components/InventoryBag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30000;

/**
 * BottomDock — floating, centered dock that anchors the 5 most-used user
 * actions to the bottom of every page when signed in. Built to solve the
 * "users can't see the Inventory Bag" discoverability issue. Visible on all
 * screen sizes; the top header still has these shortcuts for muscle memory.
 *
 * Five slots (left → right):
 *   🎒 Bag  · opens InventoryBag drawer (with unused-item badge)
 *   🔔 Bell · navigates to /profile (notifications panel lives in header)
 *   💬 Msg  · /messages (with unread-DM badge)
 *   🐦‍🔥 PT  · /profile (Phoenix Token balance)
 *   ☯️ OD   · /shop   (Omni Dollar balance)
 */
export default function BottomDock() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [bagOpen, setBagOpen] = useState(false);
  const [counts, setCounts] = useState({ bag: 0, bell: 0, msg: 0, eggs: 0 });

  const fetchAll = useCallback(async () => {
    if (!user) return;
    try {
      const [bagRes, bellRes, msgRes, nestRes] = await Promise.allSettled([
        axios.get(`${API}/inventory`),
        axios.get(`${API}/notifications/unread-count`),
        axios.get(`${API}/messages/unread`),
        axios.get(`${API}/nest/eggs`),
      ]);
      setCounts({
        bag: bagRes.status === "fulfilled"
          ? (bagRes.value.data?.totals?.unseen ?? bagRes.value.data?.totals?.unused_actionable ?? 0)
          : 0,
        bell: bellRes.status === "fulfilled" ? Number(bellRes.value.data?.unread_count || 0) : 0,
        msg: msgRes.status === "fulfilled" ? Number(msgRes.value.data?.unread || 0) : 0,
        eggs: nestRes.status === "fulfilled" ? Number(nestRes.value.data?.ready_count || 0) : 0,
      });
    } catch {
      // silent — dock degrades to zero badges
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setCounts({ bag: 0, bell: 0, msg: 0, eggs: 0 });
      return undefined;
    }
    fetchAll();
    const id = setInterval(fetchAll, POLL_MS);
    // 🎒 React instantly when the bag drawer dispatches "seen"
    const onSeen = () => {
      setCounts((c) => ({ ...c, bag: 0 }));
      fetchAll();
    };
    window.addEventListener("omni:inventory-seen", onSeen);
    return () => {
      clearInterval(id);
      window.removeEventListener("omni:inventory-seen", onSeen);
    };
  }, [user, fetchAll]);

  if (!user) return null;

  // Hide on auth/callback flows
  if (location.pathname.startsWith("/auth")) return null;

  const pt = user.tokens || 0;
  const od = user.omni_dollars || 0;
  const boundOD = user.bound_omni_dollars || 0;
  const unboundOD = user.unbound_omni_dollars != null
    ? user.unbound_omni_dollars
    : Math.max(0, od - boundOD);

  const items = [
    {
      key: "bag",
      label: "Bag",
      icon: <Backpack size={18} strokeWidth={2} />,
      onClick: () => setBagOpen(true),
      badge: counts.bag,
      testId: "dock-bag",
    },
    {
      key: "bell",
      label: "Alerts",
      icon: <Bell size={18} strokeWidth={2} />,
      onClick: () => navigate("/profile?tab=alerts"),
      badge: counts.bell,
      testId: "dock-bell",
    },
    {
      key: "msg",
      label: "Messages",
      icon: <MessageCircle size={18} strokeWidth={2} />,
      onClick: () => navigate("/messages"),
      badge: counts.msg,
      testId: "dock-messages",
    },
    {
      key: "nest",
      label: "Nest",
      icon: <Egg size={18} strokeWidth={2} />,
      onClick: () => navigate("/nest"),
      badge: counts.eggs,
      testId: "dock-nest",
    },
    {
      key: "pt",
      label: "Phoenix Tokens",
      icon: <span className="text-base leading-none">🐦‍🔥</span>,
      onClick: () => navigate("/profile?tab=wallet"),
      badge: 0,
      value: pt,
      testId: "dock-pt",
    },
    {
      key: "od",
      label: "OD",
      icon: <Coins size={18} strokeWidth={2} className="text-[#D4AF37]" />,
      onClick: () => navigate("/shop"),
      badge: 0,
      value: od,
      subValue: boundOD > 0
        ? `🔒${boundOD > 9999 ? `${Math.floor(boundOD / 1000)}k` : boundOD}`
        : null,
      subTitle: `🔒 Bound: ${boundOD.toLocaleString()} · 💵 Unbound: ${unboundOD.toLocaleString()}`,
      testId: "dock-od",
    },
    {
      key: "shopping",
      label: "List",
      icon: <ShoppingCart size={18} strokeWidth={2} />,
      onClick: () => window.dispatchEvent(new CustomEvent("omnimuscle:open-shopping")),
      badge: 0,
      testId: "dock-shopping",
    },
    {
      key: "manifesto",
      label: "Owner's Perch",
      icon: <Binoculars size={18} strokeWidth={2} className="text-purple-300" />,
      onClick: () => navigate("/corner"),
      badge: 0,
      testId: "dock-owners-perch",
    },
  ];

  return (
    <>
      <div
        data-testid="bottom-dock"
        className="fixed left-1/2 -translate-x-1/2 bottom-3 sm:bottom-5 z-40 pointer-events-none"
      >
        <div
          className="pointer-events-auto flex items-stretch gap-1 sm:gap-1.5 bg-black/85 backdrop-blur-md border border-[#D4AF37]/40 px-2 py-2 sm:px-3 sm:py-2.5 shadow-2xl"
          role="navigation"
          aria-label="Quick access dock"
        >
          {items.map((it) => (
            <DockButton
              key={it.key}
              label={it.label}
              icon={it.icon}
              onClick={it.onClick}
              badge={it.badge}
              value={it.value}
              subValue={it.subValue}
              subTitle={it.subTitle}
              testId={it.testId}
            />
          ))}
        </div>
      </div>
      <InventoryBag open={bagOpen} onClose={() => setBagOpen(false)} />
    </>
  );
}

function DockButton({ label, icon, onClick, badge, value, subValue, subTitle, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="relative group flex flex-col items-center justify-center min-w-[52px] sm:min-w-[60px] px-1 sm:px-2 py-1 text-neutral-300 hover:text-[#D4AF37] transition-colors"
      title={subTitle || label}
    >
      <div className="flex items-center justify-center h-6 sm:h-7">{icon}</div>
      <div className="font-mono tabular-nums text-[9px] sm:text-[10px] uppercase tracking-wider mt-0.5 leading-none">
        {value != null ? (
          <span className="text-white group-hover:text-[#D4AF37] font-bold">
            {value > 9999 ? `${Math.floor(value / 1000)}k` : value.toLocaleString()}
          </span>
        ) : (
          label
        )}
      </div>
      {subValue && (
        <div
          data-testid={`${testId}-sub`}
          className="font-mono tabular-nums text-[8px] sm:text-[9px] text-purple-300 leading-none mt-0.5"
        >
          {subValue}
        </div>
      )}
      {badge > 0 && (
        <span
          data-testid={`${testId}-badge`}
          className="absolute -top-1 -right-1 bg-[#DC2626] text-white text-[9px] font-bold tabular-nums min-w-[16px] h-[16px] px-1 flex items-center justify-center rounded-full border border-black leading-none"
        >
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </button>
  );
}
